import { useEffect, useState, type FormEvent } from 'react';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Input } from '../components/ui/Input';
import { IconHaken, IconPlus, IconZaehler } from '../components/icons';
import { DEMO, api, ApiFehler } from '../lib/api';
import { DEMO_OBJEKTE, DEMO_ZAEHLER, type Zaehlermedium } from '../lib/demodaten';
import { formatDatum, formatEuro, formatZahl, parseEuroEingabe, parseZahlEingabe } from '../lib/format';

const MEDIUM_META: Record<Zaehlermedium, { label: string; einheit: string }> = {
  kaltwasser: { label: 'Kaltwasser', einheit: 'm³' },
  warmwasser: { label: 'Warmwasser', einheit: 'm³' },
  waerme: { label: 'Wärme', einheit: 'kWh' },
  hkv: { label: 'Heizkostenverteiler', einheit: 'Striche' },
  strom: { label: 'Strom', einheit: 'kWh' },
  gas: { label: 'Gas', einheit: 'm³' },
};
const MEDIEN = Object.keys(MEDIUM_META) as Zaehlermedium[];

const heute = () => new Date().toISOString().slice(0, 10);
const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

interface ObjektRef {
  id: string;
  bezeichnung: string;
}
interface ZaehlerAnsicht {
  id: string;
  nummer: string;
  medium: Zaehlermedium;
  einheit: string;
  fernablesbar: boolean;
  letzterStand: number | null;
  letztesDatum: string | null;
  /** Einheit (Wohnung), der der Zähler zugeordnet ist; null = Hauptzähler des Objekts. */
  einheitId: string | null;
  einheitBezeichnung: string | null;
}

interface ApiZaehler {
  id: string;
  nummer: string;
  medium: string;
  einheit: string;
  fernablesbar: boolean;
  einheitId: string | null;
  einheitBezeichnung?: string | null;
}

export interface EinheitAuswahl {
  id: string;
  bezeichnung: string;
}

export const ANLASS_LABEL: Record<string, string> = {
  periodisch: 'Turnusablesung',
  einzug: 'Einzug',
  auszug: 'Auszug',
  zwischenablesung: 'Zwischenablesung',
  wechsel: 'Zählerwechsel',
};

export { MEDIUM_META };
interface ApiAblesung {
  datum: string;
  stand: number;
}

export function Zaehler({
  initialObjektId = null,
  onZurueck,
  eingebettet = false,
  einheiten = [],
}: {
  initialObjektId?: string | null;
  onZurueck?: () => void;
  /** In der Objektakte: ohne Seitenkopf und ohne Objektauswahl. */
  eingebettet?: boolean;
  /** Einheiten des Objekts für die Zuordnung der Zähler. */
  einheiten?: EinheitAuswahl[];
} = {}) {
  const [objekte, setObjekte] = useState<ObjektRef[]>(
    DEMO ? DEMO_OBJEKTE.map((o) => ({ id: o.id, bezeichnung: o.bezeichnung })) : [],
  );
  const [gewaehlt, setGewaehlt] = useState<string>(
    initialObjektId ?? (DEMO ? (DEMO_OBJEKTE[0]?.id ?? '') : ''),
  );
  const [zaehler, setZaehler] = useState<ZaehlerAnsicht[] | null>(null);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);

  // Objekte laden (für die Auswahl).
  useEffect(() => {
    if (DEMO || eingebettet) return;
    api
      .get<{ objekte: ObjektRef[] }>('/objekte')
      .then((d) => {
        setObjekte(d.objekte);
        setGewaehlt((g) => g || (d.objekte[0]?.id ?? ''));
      })
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Objekte konnten nicht geladen werden.'));
  }, []);

  async function ladeZaehler(objektId: string) {
    if (!objektId) {
      setZaehler([]);
      setLaden(false);
      return;
    }
    if (DEMO) {
      const name = DEMO_OBJEKTE.find((o) => o.id === objektId)?.bezeichnung;
      setZaehler(
        DEMO_ZAEHLER.filter((z) => z.objekt === name).map((z) => ({
          id: z.id,
          nummer: z.nummer,
          medium: z.medium,
          einheit: z.einheit,
          fernablesbar: z.fernablesbar,
          letzterStand: z.letzterStand,
          letztesDatum: z.letztesDatum,
          einheitId: null,
          einheitBezeichnung: null,
        })),
      );
      setLaden(false);
      return;
    }
    setLaden(true);
    try {
      const d = await api.get<{ zaehler: ApiZaehler[] }>(`/zaehler?objektId=${objektId}`);
      const mitStand = await Promise.all(
        d.zaehler.map(async (z) => {
          let letzterStand: number | null = null;
          let letztesDatum: string | null = null;
          try {
            const a = await api.get<{ ablesungen: ApiAblesung[] }>(`/zaehler/${z.id}/ablesungen`);
            const letzte = a.ablesungen[a.ablesungen.length - 1];
            if (letzte) {
              letzterStand = letzte.stand;
              letztesDatum = letzte.datum;
            }
          } catch {
            /* Ablesungen optional */
          }
          return {
            id: z.id,
            nummer: z.nummer,
            medium: z.medium as Zaehlermedium,
            einheit: z.einheit,
            fernablesbar: z.fernablesbar,
            letzterStand,
            letztesDatum,
            einheitId: z.einheitId ?? null,
            einheitBezeichnung: z.einheitBezeichnung ?? null,
          };
        }),
      );
      setZaehler(mitStand);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Zähler konnten nicht geladen werden.');
    } finally {
      setLaden(false);
    }
  }

  useEffect(() => {
    void ladeZaehler(gewaehlt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gewaehlt]);

  function ablesungGespeichert(zaehlerId: string, stand: number, datum: string) {
    setZaehler((zs) =>
      (zs ?? []).map((z) => (z.id === zaehlerId ? { ...z, letzterStand: stand, letztesDatum: datum } : z)),
    );
  }

  // Hauptzähler zuerst, dann nach Einheit.
  const sortiert = [...(zaehler ?? [])].sort(
    (a, b) =>
      Number(a.einheitId !== null) - Number(b.einheitId !== null) ||
      (a.einheitBezeichnung ?? '').localeCompare(b.einheitBezeichnung ?? '', 'de'),
  );

  return (
    <div>
      {onZurueck && !eingebettet && (
        <button onClick={onZurueck} className="mb-3 text-sm font-medium text-accent hover:underline">
          ← Zurück zu Objekte
        </button>
      )}
      {eingebettet ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-xl text-sm text-fg-muted">
            Hauptzähler des Hauses und Zähler der einzelnen Einheiten. Die Ablesungen sind die Grundlage der
            verbrauchsabhängigen Abrechnung.
          </p>
          <Button icon={<IconPlus groesse={18} />} onClick={() => setFormOffen((o) => !o)} disabled={!gewaehlt}>
            Zähler anlegen
          </Button>
        </div>
      ) : (
        <Seitenkopf
          titel="Zähler & Ablesungen"
          beschreibung="Wärme-, Wasser-, Strom- und Gaszähler des Objekts. Die Ablesungen sind die Grundlage der verbrauchsabhängigen Abrechnung (HeizkostenV: 50–70 % nach Verbrauch); fernablesbare Zähler lösen die monatliche Verbrauchsinformation aus."
          aktion={
            <Button icon={<IconPlus groesse={18} />} onClick={() => setFormOffen((o) => !o)} disabled={!gewaehlt}>
              Zähler anlegen
            </Button>
          }
        />
      )}

      {objekte.length > 0 && !eingebettet && (
        <div className="mb-4 max-w-xs">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Objekt</span>
            <select className={feldKlasse} value={gewaehlt} onChange={(e) => setGewaehlt(e.target.value)}>
              {objekte.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.bezeichnung}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {formOffen && gewaehlt && (
        <ZaehlerFormular
          objektId={gewaehlt}
          einheiten={einheiten}
          onAngelegt={(z) => {
            setZaehler((zs) => [z, ...(zs ?? [])]);
            setFormOffen(false);
          }}
          onAbbruch={() => setFormOffen(false)}
        />
      )}

      {laden && !zaehler ? (
        <p className="text-sm text-fg-muted">Zähler werden geladen …</p>
      ) : fehler && !zaehler ? (
        <Card>
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      ) : zaehler && zaehler.length === 0 ? (
        <EmptyState
          icon={<IconZaehler groesse={22} />}
          titel="Noch kein Zähler erfasst"
          beschreibung="Legen Sie die Wärme-, Wasser-, Strom- und Gaszähler dieses Objekts an und erfassen Sie deren Stände."
          aktion={<Button icon={<IconPlus groesse={18} />} onClick={() => setFormOffen(true)}>Zähler anlegen</Button>}
        />
      ) : (
        <div className="space-y-3">
          {sortiert.map((z) => (
            <ZaehlerKarte key={z.id} z={z} onAblesung={ablesungGespeichert} />
          ))}
        </div>
      )}

      {gewaehlt && <HeizkostenAbschnitt objektId={gewaehlt} />}
    </div>
  );
}

function ZaehlerKarte({
  z,
  onAblesung,
}: {
  z: ZaehlerAnsicht;
  onAblesung: (id: string, stand: number, datum: string) => void;
}) {
  const [offen, setOffen] = useState(false);
  const meta = MEDIUM_META[z.medium];
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-md bg-accent-soft text-accent-soft-fg">
            <IconZaehler groesse={17} />
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-fg">{z.nummer}</span>
              <Badge ton="neutral">{meta?.label ?? z.medium}</Badge>
              <Badge ton={z.einheitId ? 'info' : 'neutral'}>{z.einheitBezeichnung ?? 'Hauptzähler'}</Badge>
              {z.fernablesbar && <Badge ton="info">fernablesbar</Badge>}
            </div>
            <p className="mt-1 text-sm text-fg-muted">
              {z.letzterStand === null
                ? 'Noch keine Ablesung erfasst'
                : `Letzter Stand: ${formatZahl(z.letzterStand)} ${z.einheit}${z.letztesDatum ? ` · ${formatDatum(z.letztesDatum)}` : ''}`}
            </p>
          </div>
        </div>
        <Button variante="sekundaer" groesse="sm" onClick={() => setOffen((o) => !o)}>
          Ablesung erfassen
        </Button>
      </div>

      {offen && (
        <AblesungFormular
          zaehler={z}
          onGespeichert={(stand, datum) => {
            onAblesung(z.id, stand, datum);
            setOffen(false);
          }}
        />
      )}
    </Card>
  );
}

export function AblesungFormular({
  zaehler,
  onGespeichert,
  vorgabeDatum,
  vorgabeAnlass = 'periodisch',
}: {
  zaehler: { id: string; einheit: string };
  onGespeichert: (stand: number, datum: string, anlass: string) => void;
  vorgabeDatum?: string;
  vorgabeAnlass?: string;
}) {
  const [datum, setDatum] = useState(vorgabeDatum ?? heute());
  const [anlass, setAnlass] = useState(vorgabeAnlass);
  const [stand, setStand] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    const standZahl = parseZahlEingabe(stand) ?? Number.NaN;
    if (!datum || !(standZahl >= 0) || Number.isNaN(standZahl)) {
      setFehler('Bitte ein Datum und einen gültigen Zählerstand angeben.');
      return;
    }
    setSendet(true);
    try {
      if (!DEMO) {
        await api.post(`/zaehler/${zaehler.id}/ablesungen`, { datum, stand: standZahl, anlass });
      }
      onGespeichert(standZahl, datum, anlass);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Ablesung konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <form onSubmit={speichern} className="mt-3 border-t border-border pt-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
        <Input label="Datum" type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-fg">Anlass</span>
          <select className={feldKlasse} value={anlass} onChange={(e) => setAnlass(e.target.value)}>
            {Object.entries(ANLASS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <Input
          label={`Stand (${zaehler.einheit})`}
          inputMode="decimal"
          value={stand}
          onChange={(e) => setStand(e.target.value)}
        />
        <Button type="submit" disabled={sendet}>
          {sendet ? '…' : 'Speichern'}
        </Button>
      </div>
      {fehler && <p className="mt-2 text-sm font-medium text-danger">{fehler}</p>}
    </form>
  );
}

/* ------------------------------------------------------------------ Zähler anlegen */

export function ZaehlerFormular({
  objektId,
  einheiten = [],
  festeEinheit,
  onAngelegt,
  onAbbruch,
}: {
  objektId: string;
  einheiten?: EinheitAuswahl[];
  /** In der Einheitenakte: Zähler gehört fest zu dieser Einheit. */
  festeEinheit?: EinheitAuswahl;
  onAngelegt: (z: ZaehlerAnsicht) => void;
  onAbbruch: () => void;
}) {
  const [zuordnung, setZuordnung] = useState<string>(festeEinheit?.id ?? '');
  const [medium, setMedium] = useState<Zaehlermedium>('waerme');
  const [nummer, setNummer] = useState('');
  const [einheit, setEinheit] = useState(MEDIUM_META.waerme.einheit);
  const [fernablesbar, setFernablesbar] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  function mediumWechsel(m: Zaehlermedium) {
    setMedium(m);
    setEinheit(MEDIUM_META[m].einheit);
  }

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!nummer.trim() || !einheit.trim()) {
      setFehler('Bitte Zählernummer und Einheit angeben.');
      return;
    }
    setSendet(true);
    try {
      if (!DEMO) {
        await api.post<{ zaehler: ApiZaehler }>('/zaehler', {
          objektId,
          einheitId: zuordnung || undefined,
          nummer: nummer.trim(),
          medium,
          einheit: einheit.trim(),
          fernablesbar,
        });
      }
      onAngelegt({
        id: `neu-${Date.now()}`,
        nummer: nummer.trim(),
        medium,
        einheit: einheit.trim(),
        fernablesbar,
        letzterStand: null,
        letztesDatum: null,
        einheitId: zuordnung || null,
        einheitBezeichnung:
          festeEinheit?.bezeichnung ?? einheiten.find((x) => x.id === zuordnung)?.bezeichnung ?? null,
      });
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Der Zähler konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel="Neuer Zähler" className="mb-4">
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Medium</span>
            <select className={feldKlasse} value={medium} onChange={(e) => mediumWechsel(e.target.value as Zaehlermedium)}>
              {MEDIEN.map((m) => (
                <option key={m} value={m}>
                  {MEDIUM_META[m].label}
                </option>
              ))}
            </select>
          </label>
          <Input label="Einheit" value={einheit} onChange={(e) => setEinheit(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Zählernummer" placeholder="z. B. WMZ-EG-01" value={nummer} onChange={(e) => setNummer(e.target.value)} />
          {!festeEinheit && (
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-fg">Zuordnung</span>
              <select className={feldKlasse} value={zuordnung} onChange={(e) => setZuordnung(e.target.value)}>
                <option value="">Hauptzähler (ganzes Haus)</option>
                {einheiten.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.bezeichnung}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-fg">
          <input type="checkbox" checked={fernablesbar} onChange={(e) => setFernablesbar(e.target.checked)} />
          Fernablesbar (Pflicht für neu eingebaute Wärmezähler ab 2027)
        </label>

        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : 'Zähler speichern'}
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

/* ------------------------------------------------------------------ Heizkosten (Messdienst) */

const MESSDIENSTE = ['techem', 'ista', 'brunata', 'minol', 'sonstige'] as const;

interface HeizkostenEintrag {
  id: string;
  messdienst: string | null;
  zeitraumVon: string;
  zeitraumBis: string;
  gesamtHeizkostenCent: number;
  gesamtWwKostenCent: number;
  co2KostenCent: number;
}

function HeizkostenAbschnitt({ objektId }: { objektId: string }) {
  const [liste, setListe] = useState<HeizkostenEintrag[] | null>(DEMO ? [] : null);
  const [offen, setOffen] = useState(false);

  async function laden() {
    if (DEMO) {
      setListe([]);
      return;
    }
    try {
      const d = await api.get<{ heizkosten: HeizkostenEintrag[] }>(`/heizkosten?objektId=${objektId}`);
      setListe(d.heizkosten);
    } catch {
      setListe([]);
    }
  }

  useEffect(() => {
    void laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objektId]);

  return (
    <div className="mt-8">
      <div className="mb-2 flex items-center justify-between px-1">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
          Heizkosten des Messdiensts
        </h2>
        <Button variante="sekundaer" groesse="sm" icon={<IconPlus groesse={16} />} onClick={() => setOffen((o) => !o)}>
          Heizkosten erfassen
        </Button>
      </div>

      <p className="mb-3 px-1 text-xs text-fg-muted">
        Die Gesamtkosten aus der Abrechnung des Messdiensts (Techem, ista …) — Grundlage der
        verbrauchsabhängigen Heizkostenabrechnung und der CO₂-Aufteilung.
      </p>

      {offen && (
        <HeizkostenFormular
          objektId={objektId}
          onGespeichert={(h) => {
            setListe((l) => [h, ...(l ?? [])]);
            setOffen(false);
          }}
          onAbbruch={() => setOffen(false)}
        />
      )}

      {liste && liste.length > 0 ? (
        <div className="space-y-2">
          {liste.map((h) => (
            <Card key={h.id}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-fg">
                    {formatDatum(h.zeitraumVon)} – {formatDatum(h.zeitraumBis)}
                  </p>
                  <p className="mt-0.5 text-xs text-fg-muted">
                    Messdienst: {h.messdienst ?? '—'} · CO₂-Kosten {formatEuro(h.co2KostenCent)}
                  </p>
                </div>
                <p className="text-sm font-semibold tabellenziffern text-fg">
                  {formatEuro(h.gesamtHeizkostenCent + h.gesamtWwKostenCent)}
                </p>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        !offen && <p className="px-1 text-sm text-fg-muted">Noch keine Heizkosten erfasst.</p>
      )}
    </div>
  );
}

function HeizkostenFormular({
  objektId,
  onGespeichert,
  onAbbruch,
}: {
  objektId: string;
  onGespeichert: (h: HeizkostenEintrag) => void;
  onAbbruch: () => void;
}) {
  const jahr = new Date().getFullYear() - 1;
  const [messdienst, setMessdienst] = useState('techem');
  const [von, setVon] = useState(`${jahr}-01-01`);
  const [bis, setBis] = useState(`${jahr}-12-31`);
  const [heiz, setHeiz] = useState('');
  const [ww, setWw] = useState('');
  const [co2Kg, setCo2Kg] = useState('');
  const [co2Kosten, setCo2Kosten] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    const heizCent = parseEuroEingabe(heiz);
    if (heizCent === null || heizCent < 0) {
      setFehler('Bitte die Gesamt-Heizkosten als Betrag angeben.');
      return;
    }
    const wwCent = parseEuroEingabe(ww) ?? 0;
    const co2KostenCent = parseEuroEingabe(co2Kosten) ?? 0;
    const co2MengeKg = co2Kg.trim() ? Number(co2Kg.replace(',', '.')) : null;

    setSendet(true);
    try {
      if (!DEMO) {
        await api.post('/heizkosten', {
          objektId,
          messdienst,
          zeitraumVon: von,
          zeitraumBis: bis,
          gesamtHeizkostenCent: heizCent,
          gesamtWwKostenCent: wwCent,
          co2KostenCent,
          co2MengeKg: co2MengeKg ?? undefined,
        });
      }
      onGespeichert({
        id: `neu-${Date.now()}`,
        messdienst,
        zeitraumVon: von,
        zeitraumBis: bis,
        gesamtHeizkostenCent: heizCent,
        gesamtWwKostenCent: wwCent,
        co2KostenCent,
      });
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Heizkosten konnten nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel="Heizkosten erfassen" className="mb-3">
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Messdienst</span>
            <select className={feldKlasse} value={messdienst} onChange={(e) => setMessdienst(e.target.value)}>
              {MESSDIENSTE.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <Input label="Zeitraum von" type="date" value={von} onChange={(e) => setVon(e.target.value)} />
          <Input label="Zeitraum bis" type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Gesamt-Heizkosten (€)" inputMode="decimal" value={heiz} onChange={(e) => setHeiz(e.target.value)} />
          <Input label="Warmwasserkosten (€)" inputMode="decimal" placeholder="optional" value={ww} onChange={(e) => setWw(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="CO₂-Menge (kg)" inputMode="decimal" placeholder="optional" value={co2Kg} onChange={(e) => setCo2Kg(e.target.value)} />
          <Input label="CO₂-Kosten (€)" inputMode="decimal" placeholder="optional" value={co2Kosten} onChange={(e) => setCo2Kosten(e.target.value)} />
        </div>

        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : 'Heizkosten speichern'}
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default Zaehler;
